<template>
  <div id="MainData">
    <vx-card class="shadow-sm" :title="$t('PropertyDetails')">
      
      <!-- 1. البيانات الأساسية -->
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <h6 class="mb-4 text-primary"><i class="feather icon-info mr-2"></i>{{ $t('BasicInformation') }}</h6>
        </div>
        
        <!-- كود العقار -->
        <div class="vx-col md:w-1/4 w-full mb-4" v-if="property.Id">
          <label class="text-sm opacity-75">{{ $t('propertyCode') }}</label>
          <vs-input disabled v-model="property.OwnerCode" class="w-full mt-1" placeholder="Ex: PROP-001" />
        </div>
        
        <!-- اسم العقار بالعربي -->
        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('propertyNameAr') }} *</label>
          <vs-input v-model="property.PropertyNameAr" class="w-full mt-1" :danger="!property.PropertyNameAr" placeholder="اسم العقار بالعربي" />
          <span class="text-danger text-xs" v-if="!property.PropertyNameAr">{{ RequiredMessage }}</span>
        </div>

        <!-- اسم العقار بالإنجليزي -->
        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('propertyNameEn') }}</label>
          <vs-input v-model="property.PropertyNameEn" class="w-full mt-1" placeholder="Property Name (EN)" />
        </div>

        <!-- اختيار مالك العقار (الحقل الجديد) -->
        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t("propertyOwner") }} *</label>
          <v-select 
            class="mt-1"
            :options="PropertyOwners" 
            :label="currentLocale === 'ar' ? 'FullNameAr' : 'FullNameEn'" 
            v-model="property.PropertyOwnerId" 
            :reduce="owner => owner.Id" 
            :placeholder="$t('SelectPropertyOwner')"
          />
          <span class="text-danger text-xs" v-if="!property.PropertyOwnerId">{{ RequiredMessage }}</span>
        </div>

        <!-- نوع العقار -->
        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t("propertyType") }} *</label>
          <v-select 
            class="mt-1"
            :options="PropertyTypes" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.PropertyTypeId" 
            :reduce="t => t.ID" 
          />
          <span class="text-danger text-xs" v-if="!property.PropertyTypeId">{{ RequiredMessage }}</span>
        </div>

        <!-- حالة العرض/الإدراج -->
        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t("PropertyListingStatus") }} *</label>
          <v-select 
            class="mt-1"
            :options="PropertyListingStatus" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.PropertyListingStatusId" 
            :reduce="t => t.Id" 
          />
        </div>
      </div>

      <vs-divider />

      <!-- 2. تفاصيل الموقع -->
      <div class="vx-row mb-6">
        <div class="vx-col w-full">
          <h6 class="mb-4 text-primary"><i class="feather icon-map-pin mr-2"></i>{{ $t('LocationDetails') }}</h6>
        </div>

        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t("country") }} *</label>
          <v-select 
            class="mt-1"
            :options="Countries" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.CountryId" 
            :reduce="c => c.ID" 
            @input="onCountryChange"
          />
        </div>

        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('region') }}</label>
          <v-select 
            class="mt-1"
            :options="Regions" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.RegionId" 
            :reduce="r => r.Id" 
            :disabled="!property.CountryId"
            @input="onRegionChange"
          />
        </div>

        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t("city") }} *</label>
          <v-select 
            class="mt-1"
            :options="Cities" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.CityId" 
            :reduce="c => c.Id" 
            :disabled="!property.RegionId"
            @input="onCityChange"
          />
        </div>

        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('district') }}</label>
          <v-select 
            :options="Districts" 
            :label="currentLocale === 'ar' ? 'NameAr' : 'NameEn'" 
            v-model="property.DistrictId" 
            :reduce="d => d.Id" 
            :disabled="!property.CityId"
            placeholder="Select District"
          />
        </div>
      </div>

      <vs-divider />

      <!-- 3. تفاصيل إضافية -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <h6 class="mb-4 text-primary"><i class="feather icon-file-text mr-2"></i>{{ $t('AdditionalDetails') }}</h6>
        </div>

        <div class="vx-col md:w-1/3 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('titleDeedNo') }}</label>
          <vs-input v-model="property.TitleDeedNumber" class="w-full mt-1" />
        </div>

        <div class="vx-col md:w-1/3 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('estimatedPropertyValue') }}</label>
          <vs-input type="number" v-model.number="property.EstimatedPropertyValue" class="w-full mt-1" icon-pack="feather" icon="icon-dollar-sign" />
        </div>

        <div class="vx-col md:w-1/4 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('commissionPercentage') }} *</label>
          <vs-input type="number" min="0" max="100" v-model.number="property.CommissionPercentage" class="w-full mt-1" icon-pack="feather" icon="icon-percent" placeholder="0.00" />
          <span class="text-danger text-xs" v-if="property.CommissionPercentage === null || property.CommissionPercentage === ''">{{ RequiredMessage }}</span>
        </div>

        <div class="vx-col md:w-1/3 w-full mb-4">
          <label class="text-sm opacity-75">{{ $t('shortDescription') }}</label>
          <vs-input v-model="property.ShortDescriptionAr" class="w-full mt-1" />
        </div>
      </div>

    </vx-card>

    <!-- الأزرار -->
    <div class="flex flex-wrap items-center justify-end mt-6">
      <vs-button type="border" color="rgba(0,0,0,0.5)" icon-pack="feather" icon="icon-x" @click="cancel" class="mr-4 rtl:ml-4 rtl:mr-0">
        {{ $t("Cancel") }}
      </vs-button>
      <vs-button icon-pack="feather" icon="icon-save" :disabled="!isFormValid" @click="submitData">
        {{ $t("SaveChanges") }}
      </vs-button>
    </div>
  </div>
</template>

<script>
import VxCard from "../../components/vx-card/VxCard.vue";
import { domain } from "@/gloabelConstant.js";
import Datepicker from "vuejs-datepicker";
import vSelect from "vue-select";
import moduleProperties from "@/store/Properties/moduleProperties.js";
import modulePropertyOwners from "@/store/PropertyOwners/modulePropertyOwners.js";
import modulePropertyType from "@/store/settings/PropertyType/modulePropertyType.js";
import moduleDistrict from "@/store/settings/District/moduleDistrict.js";
import moduleCity from "@/store/settings/City/moduleCity.js";
import moduleCountries from "@/store/settings/Country/moduleCountry.js";
import moduleRegion from "@/store/settings/Region/moduleRegion.js";

export default {
  components: {
    VxCard,
    Datepicker,
    vSelect
  },
  props: {
    property: {
      type: Object,
      required: true,
    }
  },

  data() {
    return {
      baseURL: domain,
      userInfo: {},
      RequiredMessage: "This Field Is Required",
    };
  },

  computed: {
    currentLocale() { return this.$i18n.locale; },
    PropertyOwners() { return this.$store.state.PropertyOwnersList.PropertyOwners || []; },
    PropertyListingStatus() { return this.$store.state.PropertiesList.PropertyListingStatus; },
    PropertyTypes() { return this.$store.state.PropertyTypesList.items; },
    Districts() { return this.$store.state.DistrictList.items; },
    Cities() { return this.$store.state.CityList.items; },
    Countries() { return this.$store.state.CountriesList.countries; },
    Regions() { return this.$store.state.RegionList.items; },

    isFormValid() {
      const basicValidation =
        !this.errors.any() &&
        this.property.PropertyNameAr &&
        this.property.PropertyNameEn && 
        this.property.PropertyOwnerId && // تم إضافة المالك للتحقق
        this.property.PropertyTypeId &&
        this.property.PropertyListingStatusId &&
        this.property.CountryId &&
        this.property.RegionId &&
        this.property.CityId &&
        this.property.DistrictId &&
        this.property.EstimatedPropertyValue;

      return basicValidation;
    }
  },

  methods: {
    onCountryChange() {
      this.property.RegionId = null;
      this.property.CityId = null;
      this.property.DistrictId = null;
      
      if (this.property.CountryId != null) {
        this.$vs.loading();
        this.$store.dispatch("RegionList/fetchRegionsByCountry", this.property.CountryId)
          .then(() => { this.$vs.loading.close(); })
          .catch(() => { this.$vs.loading.close(); });
      }
    },

    onRegionChange() {
      this.property.CityId = null;
      this.property.DistrictId = null;
      
      if (this.property.RegionId != null) {
        this.$vs.loading();
        this.$store.dispatch("CityList/fetchCitiesByRegion", this.property.RegionId)
          .then(() => { this.$vs.loading.close(); })
          .catch(() => { this.$vs.loading.close(); });
      }
    },

    onCityChange() {
      this.property.DistrictId = null;
      
      if (this.property.CityId != null) {
        this.$vs.loading();
        this.$store.dispatch("DistrictList/fetchDistrictsByCity", this.property.CityId)
          .then(() => { this.$vs.loading.close(); })
          .catch(() => { this.$vs.loading.close(); });
      }
    },

    initValues() {
      this.property = {
        Id: 0,
        PropertyCode: "",
        PropertyNameAr: "",
        PropertyNameEn: "",
        PropertyOwnerId: null,
        PropertyTypeId: null,
        CountryId: null,
        CityId: null,
        GovernorateId: null,
        DistrictId: null,
        RegionId: null,
        TitleDeedNo: "",
        EstimatedPropertyValue: null,
        ShortDescriptionAr: "",
      };
    },

    submitData() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const actionName = (this.property.Id && this.property.Id > 0) 
                             ? "PropertiesList/updateItem" 
                             : "PropertiesList/addItem";

          this.$vs.loading();

          this.$store.dispatch(actionName, this.property)
            .then(() => {
              this.$vs.loading.close();     
              this.showSuccess();
              return this.$store.dispatch("PropertiesList/fetchDataListItems");
            })
            .then(() => {
              this.cancel(); 
            })
            .catch((err) => {
              this.$vs.loading.close();

              let errorMessage = "An error occurred";
              if (err.response && err.response.data && err.response.data.message) {
                errorMessage = err.response.data.message;
              }

              this.$vs.notify({
                time: 2500,
                title: "Error",
                text: errorMessage,
                iconPack: "feather",
                icon: "icon-alert-circle",
                color: "danger",
              });
            });
        }
      });
    },

    showSuccess() {
      this.$vs.notify({
        time: 2500,
        title: "Success",
        text: "Saved Successfully",
        iconPack: "feather",
        icon: "icon-alert-circle",
        color: "success",
      });
    },

    reset_data() {
      this.initValues();
    },

    cancel() {
      this.$router.go(-1);
    },
  },

  created() {
    this.userInfo = JSON.parse(localStorage.getItem("userInfo"));

    if (!moduleProperties.isRegistered) {
      this.$store.registerModule("PropertiesList", moduleProperties);
      moduleProperties.isRegistered = true;
    }


    if (!modulePropertyOwners.isRegistered) {
      this.$store.registerModule("PropertyOwnersList", modulePropertyOwners);
      modulePropertyOwners.isRegistered = true;
    }
   
    if (!modulePropertyType.isRegistered) {
      this.$store.registerModule("PropertyTypesList", modulePropertyType);
      modulePropertyType.isRegistered = true;
    }

    if (!moduleDistrict.isRegistered) {
      this.$store.registerModule("DistrictList", moduleDistrict);
      moduleDistrict.isRegistered = true;
    }
    
    if (!moduleCity.isRegistered) {
      this.$store.registerModule("CityList", moduleCity);
      moduleCity.isRegistered = true;
    }

    if (!moduleCountries.isRegistered) {
      this.$store.registerModule("CountriesList", moduleCountries);
      moduleCountries.isRegistered = true;
    }

    if (!moduleRegion.isRegistered) {
      this.$store.registerModule("RegionList", moduleRegion);
      moduleRegion.isRegistered = true;
    }

    // جلب قائمة المالكين
    this.$store.dispatch("PropertyOwnersList/fetchDataListItems");
    this.$store.dispatch("PropertiesList/fetchDataListItems");
    this.$store.dispatch("PropertiesList/fetchPropertyListingStatus");
    this.$store.dispatch("PropertyTypesList/fetchDataListItems");
    this.$store.dispatch("CountriesList/fetchDataListItems");
  },

  watch: {
    property: {
      immediate: true,
      deep: true,
      handler(val) {
        if (!val || !val.CountryId) return;

        this.$store.dispatch("RegionList/fetchRegionsByCountry", val.CountryId);
      }
    }
  },

  mounted() {
    this.isMounted = true;
  }
};
</script>

<style lang="scss">
#MainData {
  .vs-input--input, .vs__dropdown-toggle, .vdp-datepicker input {
    border: 1px solid rgba(0, 0, 0, 0.1) !important;
    border-radius: 6px !important; 
    height: 38px !important; 
    padding: 0.6rem !important;
    box-shadow: none !important;
    font-size: 0.9rem;
  }

  label {
    font-weight: 500;
    color: #555;
    margin-bottom: 4px;
    display: block;
    font-size: 0.85rem;
  }

  .vx-row {
    margin-bottom: 0.5rem !important;
  }

  .text-danger {
    font-size: 0.75rem;
    margin-top: 2px;
    display: block;
  }

  .vdp-datepicker__calendar {
    position: absolute;
    z-index: 999999 !important;
    bottom: 100%;
    left: 0;
    background: white;
    box-shadow: 0 5px 25px rgba(0,0,0,0.1);
    border: 1px solid #ddd !important;
    border-radius: 8px;
  }

  .vx-card {
    overflow: visible !important;
  }

  .vx-card__collapsible-content {
    overflow: visible !important;
  }
}
</style>